import type { Platform } from '@/types/site';

interface PlatformCardProps {
  platform: Platform;
  rank?: number;
}

export default function PlatformCard({ platform, rank }: PlatformCardProps) {
  return (
    <article className="group flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300">
      <div className="flex items-start gap-3">
        <span className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-background-100 font-heading text-xl font-bold text-primary-700">
          {platform.short}
          {rank !== undefined && (
            <span className="absolute -left-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-accent-500 text-[11px] font-semibold text-primary-950">
              {rank}
            </span>
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h3 className="font-heading text-base font-semibold text-foreground-950">{platform.name}</h3>
            {platform.verified && (
              <span className="inline-flex items-center gap-0.5 rounded-sm bg-secondary-100 px-1.5 py-0.5 text-[11px] text-secondary-800">
                <i className="ri-verified-badge-line"></i>已核验
              </span>
            )}
          </div>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1">
              <i className="ri-folder-line"></i>
              {platform.categoryName}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-map-pin-line"></i>
              {platform.region}
            </span>
          </p>
        </div>
      </div>

      <p className="mt-4 flex-1 text-sm leading-relaxed text-foreground-600 line-clamp-3">
        {platform.description}
      </p>

      <div className="mt-4 flex flex-wrap gap-1.5">
        {platform.tags.map((tag) => (
          <span
            key={tag}
            className="rounded-sm bg-background-100 px-2 py-1 text-[11px] text-foreground-600"
          >
            {tag}
          </span>
        ))}
      </div>

      <div className="mt-5 border-t border-background-200 pt-4">
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1">
            <i className="ri-fire-line text-accent-600"></i>热度 {platform.heat}
          </span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-star-fill text-accent-500"></i>
            {platform.rating.toFixed(1)}
          </span>
          <span>核验 {platform.updatedAt}</span>
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-background-200">
          <span
            className="block h-full rounded-full bg-accent-500"
            style={{ width: `${platform.heat}%` }}
          ></span>
        </div>
        <a
          href={platform.url}
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="mt-4 inline-flex w-full items-center justify-center gap-1 whitespace-nowrap rounded-md bg-foreground-950 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
        >
          访问发布入口
          <i className="ri-external-link-line"></i>
        </a>
      </div>
    </article>
  );
}